import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import notificationService, { NotificationSubscription, NotificationPayload, NotificationStatus } from '@/services/notificationService';
import pubSubService from '@/services/pubsubService';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';

interface NotificationContextType {
  isSupported: boolean;
  status: NotificationStatus;
  subscription: NotificationSubscription | null;
  isLoading: boolean;
  subscribe: () => Promise<{ vapid: boolean; fcm: boolean }>;
  unsubscribe: () => Promise<{ vapid: boolean; fcm: boolean }>;
  requestPermission: () => Promise<NotificationPermission>;
  showLocalNotification: (payload: NotificationPayload) => Promise<void>;
  testNotifications: () => Promise<{ vapid: boolean; fcm: boolean }>;
  refreshStatus: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [status, setStatus] = useState<NotificationStatus>({
    vapidSubscribed: false,
    fcmSubscribed: false,
    fcmEnabled: true,
    permission: 'default',
    hasServiceWorker: false,
    supportsPush: false
  });
  const [subscription, setSubscription] = useState<NotificationSubscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();

  // Initialize notification service and check current state
  useEffect(() => {
    const initializeNotifications = async () => {
      setIsLoading(true);
      
      try {
        // Check if notifications are supported
        const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
        setIsSupported(supported);

        if (!supported) {
          setIsLoading(false);
          return;
        }

        // Wait for auth to complete, then check if user is authenticated
        if (authLoading) {
          console.log('[NotificationContext] Auth still loading, waiting...');
          setIsLoading(false);
          return;
        }

        // Only initialize if user is authenticated
        if (!user) {
          console.log('[NotificationContext] User not authenticated, skipping notification initialization');
          setIsLoading(false);
          return;
        }

        console.log('[NotificationContext] User authenticated, initializing notifications');

        // Initialize the service (this will call authenticated API endpoints if needed)
        await notificationService.init();

        // Get comprehensive status
        await refreshStatus();

      } catch (error) {
        console.error('[NotificationContext] Failed to initialize:', error);
        toast({
          title: 'Notification Setup Error',
          description: 'Failed to initialize push notifications',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };

    initializeNotifications();
  }, [toast, user, authLoading]);

  // Handle user login after initial load - refresh authenticated data
  useEffect(() => {
    const refreshAfterLogin = async () => {
      // Only if notifications are already initialized but we now have a user
      if (isSupported && !authLoading && user && isLoading === false) {
        console.log('[NotificationContext] User logged in, refreshing notification status');
        try {
          await refreshStatus();
        } catch (error) {
          console.error('[NotificationContext] Failed to refresh after login:', error);
        }
      }
    };

    refreshAfterLogin();
  }, [user, authLoading, isSupported, isLoading]); // React to user changes

  // Refresh notification status
  const refreshStatus = async (): Promise<void> => {
    try {
      const newStatus = await notificationService.refreshStatus();
      setStatus(newStatus);
      
      // Update subscription info
      const newSubscription = await notificationService.getVAPIDSubscription();
      setSubscription(newSubscription);
    } catch (error) {
      console.error('[NotificationContext] Failed to refresh status:', error);
    }
  };

  // Listen for notification click events
  useEffect(() => {
    const handleNotificationClick = (event: CustomEvent) => {
      const { action, url, tokenKey, transactionId } = event.detail || {};
      
      console.log('[NotificationContext] Notification clicked:', event.detail);
      
      // Publish notification click event to Pub/Sub
      pubSubService.publishNotificationClicked(event.detail);
      
      // Handle navigation based on notification type
      if (url) {
        window.location.href = url;
      } else if (tokenKey) {
        window.location.href = `/transactions/${encodeURIComponent(tokenKey)}`;
      } else if (transactionId) {
        // Navigate to transaction details
        window.location.href = '/transactions';
      }

      // Show toast notification
      toast({
        title: 'Notification',
        description: 'Opening requested content...'
      });
    };

    const handleFCMMessage = (event: CustomEvent) => {
      const payload = event.detail;
      console.log('[NotificationContext] FCM message received:', payload);
      
      // Publish FCM message received event to Pub/Sub
      pubSubService.publishNotificationReceived(
        payload.data?.action || 'unknown',
        !document.hidden // foreground if document is visible
      );
      
      // Show toast for foreground FCM messages
      if (payload.notification) {
        toast({
          title: payload.notification.title || 'New Message',
          description: payload.notification.body || 'You have a new notification',
          variant: 'default'
        });
      }

      // Handle any specific FCM actions
      if (payload.data?.action === 'token_deposited') {
        toast({
          title: 'Payment Received! 💰',
          description: `A token for ${payload.data.currency}${payload.data.amount} was deposited to your account`,
          variant: 'default'
        });
      }
    };

    const handleServiceWorkerUpdate = () => {
      // Publish app update event to Pub/Sub
      pubSubService.publishFeatureUsage('service_worker', 'update_available');
      
      toast({
        title: 'App Update Available',
        description: 'A new version of the app is available. Please refresh.',
        variant: 'default'
      });
    };

    window.addEventListener('notificationClick', handleNotificationClick as EventListener);
    window.addEventListener('fcmMessage', handleFCMMessage as EventListener);
    window.addEventListener('swUpdate', handleServiceWorkerUpdate);

    return () => {
      window.removeEventListener('notificationClick', handleNotificationClick as EventListener);
      window.removeEventListener('fcmMessage', handleFCMMessage as EventListener);
      window.removeEventListener('swUpdate', handleServiceWorkerUpdate);
    };
  }, [toast]);

  // Subscribe to push notifications (both VAPID and FCM)
  const subscribe = async (): Promise<{ vapid: boolean; fcm: boolean }> => {
    if (!isSupported) {
      toast({
        title: 'Not Supported',
        description: 'Push notifications are not supported in this browser',
        variant: 'destructive'
      });
      return { vapid: false, fcm: false };
    }

    setIsLoading(true);
    
    try {
      const results = await notificationService.subscribe();
      
      // Publish subscription events to Pub/Sub
      if (results.vapid) {
        await pubSubService.publishNotificationSubscription('vapid', true);
      } else {
        await pubSubService.publishNotificationSubscription('vapid', false, 'Subscription failed');
      }
      
      if (results.fcm) {
        await pubSubService.publishNotificationSubscription('fcm', true);
      } else {
        await pubSubService.publishNotificationSubscription('fcm', false, 'Subscription failed');
      }
      
      if (results.vapid || results.fcm) {
        await refreshStatus();
        
        let message = 'You will now receive push notifications for important updates';
        if (results.vapid && results.fcm) {
          message += ' (Both VAPID and FCM enabled for maximum reliability)';
        } else if (results.vapid) {
          message += ' (VAPID enabled)';
        } else if (results.fcm) {
          message += ' (Firebase Cloud Messaging enabled)';
        }
        
        toast({
          title: 'Notifications Enabled',
          description: message,
          variant: 'default'
        });
        
        return { vapid: !!results.vapid, fcm: !!results.fcm };
      } else {
        toast({
          title: 'Subscription Failed',
          description:
            'Could not register for push notifications. Check that you are signed in and try again. If this persists, the platform push API may not be enabled yet.',
          variant: 'destructive'
        });
        return { vapid: false, fcm: false };
      }
    } catch (error) {
      console.error('[NotificationContext] Subscription failed:', error);
      
      // Publish error event to Pub/Sub
      await pubSubService.publishError(error as Error, { context: 'notification_subscription' });
      
      toast({
        title: 'Subscription Error',
        description: 'Failed to subscribe to push notifications',
        variant: 'destructive'
      });
      return { vapid: false, fcm: false };
    } finally {
      setIsLoading(false);
    }
  };

  // Unsubscribe from push notifications (both VAPID and FCM)
  const unsubscribe = async (): Promise<{ vapid: boolean; fcm: boolean }> => {
    setIsLoading(true);
    
    try {
      const results = await notificationService.unsubscribe();
      
      if (results.vapid || results.fcm) {
        await refreshStatus();
        
        let message = 'You will no longer receive push notifications';
        if (results.vapid && results.fcm) {
          message += ' (Both VAPID and FCM disabled)';
        } else if (results.vapid) {
          message += ' (VAPID disabled)';
        } else if (results.fcm) {
          message += ' (FCM disabled)';
        }
        
        toast({
          title: 'Notifications Disabled',
          description: message,
          variant: 'default'
        });
        
        return { vapid: results.vapid, fcm: results.fcm };
      } else {
        toast({
          title: 'Unsubscribe Failed',
          description: 'Failed to disable push notifications',
          variant: 'destructive'
        });
        return { vapid: false, fcm: false };
      }
    } catch (error) {
      console.error('[NotificationContext] Unsubscribe failed:', error);
      toast({
        title: 'Unsubscribe Error',
        description: 'An error occurred while disabling notifications',
        variant: 'destructive'
      });
      return { vapid: false, fcm: false };
    } finally {
      setIsLoading(false);
    }
  };

  // Request notification permission
  const requestPermission = async (): Promise<NotificationPermission> => {
    try {
      const newPermission = await notificationService.requestPermission();
      await refreshStatus();
      
      // Publish permission request event to Pub/Sub
      await pubSubService.publishNotificationPermissionRequest(newPermission === 'granted');
      
      if (newPermission === 'granted') {
        toast({
          title: 'Permission Granted',
          description: 'You can now enable push notifications',
          variant: 'default'
        });
      } else if (newPermission === 'denied') {
        toast({
          title: 'Permission Denied',
          description: 'Push notifications are blocked. You can enable them in your browser settings.',
          variant: 'destructive'
        });
      }
      
      return newPermission;
    } catch (error) {
      console.error('[NotificationContext] Permission request failed:', error);
      
      // Publish error event to Pub/Sub
      await pubSubService.publishError(error as Error, { context: 'notification_permission_request' });
      
      toast({
        title: 'Permission Error',
        description: 'Failed to request notification permission',
        variant: 'destructive'
      });
      return 'denied';
    }
  };

  // Show local notification (for testing)
  const showLocalNotification = async (payload: NotificationPayload): Promise<void> => {
    try {
      await notificationService.showLocalNotification(payload);
    } catch (error) {
      console.error('[NotificationContext] Local notification failed:', error);
      toast({
        title: 'Notification Error',
        description: 'Failed to show notification',
        variant: 'destructive'
      });
    }
  };

  // Test both notification systems
  const testNotifications = async (): Promise<{ vapid: boolean; fcm: boolean }> => {
    try {
      // Show local notification first
      await showLocalNotification({
        title: 'Local Test Notification',
        body: 'This is a local test notification from VAIU',
        data: { action: 'test', source: 'local' }
      });

      // Test both VAPID and FCM backend notifications
      const results = await notificationService.sendTestNotifications();
      
      let message = 'Test notifications sent';
      if (results.vapid && results.fcm) {
        message += ' (Both VAPID and FCM)';
      } else if (results.vapid) {
        message += ' (VAPID only)';
      } else if (results.fcm) {
        message += ' (FCM only)';
      } else {
        message = 'Test notifications failed to send';
      }

      toast({
        title: 'Test Complete',
        description: message,
        variant: results.vapid || results.fcm ? 'default' : 'destructive'
      });

      return results;
    } catch (error) {
      console.error('[NotificationContext] Test notifications failed:', error);
      toast({
        title: 'Test Failed',
        description: 'Failed to send test notifications',
        variant: 'destructive'
      });
      return { vapid: false, fcm: false };
    }
  };

  const value: NotificationContextType = {
    isSupported,
    status,
    subscription,
    isLoading,
    subscribe,
    unsubscribe,
    requestPermission,
    showLocalNotification,
    testNotifications,
    refreshStatus
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use notification context
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext; 